import { api } from './api';

export interface CustomerVehicleDto {
  id: number;           // CustomerVehicle (atama) id - servis fişi vb. bu id ile bağlanır
  vehicleId?: number;   // VehicleRecord (fiziksel araç) id - araç geçmişi için
  customerId: number;
  customerName: string;
  plate: string;
  vinChassis?: string;
  brandId?: number;
  brandName?: string;
  modelId?: number;
  modelName?: string;
  make?: string;
  model?: string;
  year?: number;
  color?: string;
  engineNo?: string;
  currentKm: number;
  notes?: string;
  isActive: boolean;
  /** false if vehicle is used in service orders or documents */
  canDelete?: boolean;
}

export interface ServiceItemDto {
  id: number;
  name: string;
  description?: string;
  smsText?: string;
  defaultPrice: number;
  intervalKm?: number;
  intervalMonths?: number;
  isActive: boolean;
}

export interface ExternalServiceItemDto {
  id: number;
  name: string;
  description?: string;
  defaultPrice: number;
  isActive: boolean;
}

export interface ComplaintItemDto {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
}

export interface ServiceOrderLineDto {
  id: number;
  orderId: number;
  lineNo: number;
  itemType: number;  // 1=HİZMET, 2=PARÇA, 3=DIŞ HİZMET
  itemTypeText: string;
  serviceItemId?: number;
  serviceItemName?: string;
  externalServiceItemId?: number;
  externalServiceItemName?: string;
  externalServiceAccountId?: number;
  externalServiceAccountName?: string;
  costPrice?: number;
  productId?: number;
  productVariantId?: number;
  productCode?: string;
  productName?: string;
  technicianUserId?: number;
  technicianUserName?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  taxRate?: number;
  notes?: string;
  periodicMaintenanceMonths?: number;
}

export interface ServiceOrderNotificationDto {
  id: number;
  customerId: number;
  customerName: string;
  vehicleId: number;
  vehiclePlate: string;
  serviceOrderId: number;
  serviceOrderNo: string;
  serviceOrderLineId?: number | null;
  serviceOrderLineNo?: number | null;
  isFicheNotification: boolean;
  notificationType?: number | null;
  reminderMonths: number;
  reminderDate: string;
  status: number;
  statusText: string;
  createdAt: string;
  sentAt?: string | null;
}

export interface ServiceOrderDto {
  id: number;
  orderNo: string;
  customerId: number;
  customerName: string;
  customerPhone?: string | null;
  vehicleId: number;
  vehiclePlate: string;
  vehicleInfo?: string;
  responsibleUserId?: number;
  responsibleUserName?: string;
  deliveredByUserId?: number | null;
  deliveredByUserName?: string | null;
  entryKm?: number;
  exitKm?: number;
  orderDate: string;
  status: number;  // 1=Kabul, 2=Ön Tespit, 3=Onarımda, 4=Tamamlandı, 5=Teslim Edildi
  statusText: string;
  deliveredAt?: string | null;  // Teslim edildi tarihi (raporlama / araç kaç gün kaldı)
  totalAmount: number;
  notes?: string;
  complaintNotes?: string;
  useOrderPeriodicMaintenance?: boolean;
  orderPeriodicMaintenanceMonths?: number | null;
  /** Durum SMS (kabul, teslim vb.) bu fiş için açık mı */
  sendStatusSms?: boolean;
  complaintItems: ComplaintItemDto[];
  lines: ServiceOrderLineDto[];
  createdAt: string;
}

export interface ServiceOrderStatus {
  value: number;
  name: string;
  displayName: string;
}

// Customer Vehicles API
export async function fetchCustomerVehicles(params?: {
  customerId?: number;
  search?: string;
}): Promise<CustomerVehicleDto[]> {
  const { data } = await api.get<CustomerVehicleDto[]>('/customervehicles', { params });
  return data;
}

export async function fetchCustomerVehicle(id: number): Promise<CustomerVehicleDto> {
  const { data } = await api.get<CustomerVehicleDto>(`/customervehicles/${id}`);
  return data;
}

export async function searchCustomerVehicles(params?: {
  plate?: string;
  customerName?: string;
  phone?: string;
}): Promise<CustomerVehicleDto[]> {
  const { data } = await api.get<CustomerVehicleDto[]>('/customervehicles/search', { params });
  return data;
}

/** 409 dönünce response.data: { code: 'VehicleAssignedToOther', plate, currentCustomerName, newCustomerName } */
export const VEHICLE_ASSIGNED_TO_OTHER_CODE = 'VehicleAssignedToOther';

export async function createCustomerVehicle(vehicle: {
  customerId: number;
  plate: string;
  vinChassis?: string;
  brandId?: number;
  modelId?: number;
  make?: string;
  model?: string;
  year?: number;
  color?: string;
  engineNo?: string;
  currentKm?: number;
  notes?: string;
  reassignToNewCustomer?: boolean;
}): Promise<CustomerVehicleDto> {
  const { data } = await api.post<CustomerVehicleDto>('/customervehicles', vehicle);
  return data;
}

export async function updateCustomerVehicle(id: number, vehicle: {
  plate: string;
  vinChassis?: string;
  brandId?: number;
  modelId?: number;
  make?: string;
  model?: string;
  year?: number;
  color?: string;
  engineNo?: string;
  currentKm: number;
  notes?: string;
}): Promise<CustomerVehicleDto> {
  const { data } = await api.put<CustomerVehicleDto>(`/customervehicles/${id}`, vehicle);
  return data;
}

export async function deleteCustomerVehicle(id: number): Promise<void> {
  await api.delete(`/customervehicles/${id}`);
}

export async function getVehicleServiceHistory(vehicleId: number): Promise<any[]> {
  const { data } = await api.get<any[]>(`/customervehicles/${vehicleId}/service-history`);
  return data;
}

// Service Items API
export async function fetchServiceItems(isActive?: boolean): Promise<ServiceItemDto[]> {
  const params: any = {};
  if (isActive !== undefined) params.isActive = isActive;
  
  const { data } = await api.get<ServiceItemDto[]>('/serviceitems', { params });
  return data;
}

export async function fetchServiceItem(id: number): Promise<ServiceItemDto> {
  const { data } = await api.get<ServiceItemDto>(`/serviceitems/${id}`);
  return data;
}

export async function createServiceItem(item: {
  name: string;
  description?: string;
  smsText?: string;
  defaultPrice?: number;
  intervalKm?: number;
  intervalMonths?: number;
}): Promise<ServiceItemDto> {
  const { data } = await api.post<ServiceItemDto>('/serviceitems', item);
  return data;
}

export async function updateServiceItem(id: number, item: {
  name: string;
  description?: string;
  smsText?: string;
  defaultPrice: number;
  intervalKm?: number;
  intervalMonths?: number;
  isActive: boolean;
}): Promise<ServiceItemDto> {
  const { data } = await api.put<ServiceItemDto>(`/serviceitems/${id}`, item);
  return data;
}

export async function deleteServiceItem(id: number): Promise<void> {
  await api.delete(`/serviceitems/${id}`);
}

export async function fetchServiceOrderNotifications(params?: {
  status?: number;
  fromDate?: string;
  toDate?: string;
}): Promise<ServiceOrderNotificationDto[]> {
  const { data } = await api.get<ServiceOrderNotificationDto[]>('/serviceordernotifications', {
    params,
  });
  return data;
}

export async function cancelServiceOrderNotification(id: number): Promise<void> {
  await api.put(`/serviceordernotifications/${id}/cancel`);
}

export async function deleteServiceOrderNotification(id: number): Promise<void> {
  await api.delete(`/serviceordernotifications/${id}`);
}

export async function sendBulkUpcomingNotifications(): Promise<{
  sent: number;
  failed: number;
  skipped: number;
  total: number;
  errors?: string[];
}> {
  const { data } = await api.post<{
    sent: number;
    failed: number;
    skipped: number;
    total: number;
    errors?: string[];
  }>('/serviceordernotifications/send-upcoming');
  return data;
}

// Service Orders API
export async function fetchServiceOrders(params?: {
  customerId?: number;
  vehicleId?: number;
  status?: number | null;
  onlyOpen?: boolean;
  deliveredAtDate?: string;
  customerName?: string;
  plate?: string;
  orderDateFrom?: string;
  orderDateTo?: string;
}): Promise<ServiceOrderDto[]> {
  const query: Record<string, string | number | boolean> = {};
  if (params?.customerId != null) query.customerId = params.customerId;
  if (params?.vehicleId != null) query.vehicleId = params.vehicleId;
  if (params?.status != null && Number.isFinite(params.status)) query.status = params.status;
  if (params?.onlyOpen === true) query.onlyOpen = true;
  if (params?.deliveredAtDate) query.deliveredAtDate = params.deliveredAtDate;
  if (params?.customerName) query.customerName = params.customerName;
  if (params?.plate) query.plate = params.plate;
  if (params?.orderDateFrom) query.orderDateFrom = params.orderDateFrom;
  if (params?.orderDateTo) query.orderDateTo = params.orderDateTo;
  const { data } = await api.get<ServiceOrderDto[]>('/serviceorders', { params: query });
  return data;
}

export async function fetchServiceOrder(id: number): Promise<ServiceOrderDto> {
  const { data } = await api.get<ServiceOrderDto>(`/serviceorders/${id}`);
  return data;
}

export async function createServiceOrder(order: {
  customerId: number;
  vehicleId: number;
  responsibleUserId?: number;
  deliveredByUserId?: number;
  entryKm?: number;
  orderDate: string;
  status?: number;
  notes?: string;
  complaintNotes?: string;
  complaintItemIds?: number[];
  useOrderPeriodicMaintenance?: boolean;
  orderPeriodicMaintenanceMonths?: number | null;
  sendStatusSms?: boolean;
  lines: Array<{
    lineNo?: number;
    itemType: number;
    serviceItemId?: number;
    externalServiceItemId?: number;
    externalServiceAccountId?: number;
    costPrice?: number;
    productId?: number;
    productVariantId?: number;
    technicianUserId?: number;
    quantity: number;
    unitPrice: number;
    taxRate?: number;
    notes?: string;
    periodicMaintenanceMonths?: number;
  }>;
}): Promise<ServiceOrderDto> {
  const { data } = await api.post<ServiceOrderDto>('/serviceorders', order);
  return data;
}

export async function updateServiceOrder(id: number, order: {
  responsibleUserId?: number;
  deliveredByUserId?: number;
  entryKm?: number;
  exitKm?: number;
  orderDate: string;
  status: number;
  notes?: string;
  complaintNotes?: string;
  complaintItemIds?: number[];
  useOrderPeriodicMaintenance?: boolean;
  orderPeriodicMaintenanceMonths?: number | null;
  sendStatusSms?: boolean;
}): Promise<ServiceOrderDto> {
  const { data } = await api.put<ServiceOrderDto>(`/serviceorders/${id}`, order);
  return data;
}

export async function deleteServiceOrder(id: number): Promise<void> {
  await api.delete(`/serviceorders/${id}`);
}

export async function cancelServiceOrder(
  id: number,
  copyToNewOrder: boolean
): Promise<{ cancelledOrderId: number; newOrderId?: number | null; warning?: string | null }> {
  const { data } = await api.post<{ cancelledOrderId: number; newOrderId?: number | null; warning?: string | null }>(
    `/serviceorders/${id}/cancel`,
    { copyToNewOrder }
  );
  return data;
}

export async function getServiceOrderStatuses(): Promise<ServiceOrderStatus[]> {
  const { data } = await api.get<Record<string, unknown>[]>('/serviceorders/statuses');
  const rows = Array.isArray(data) ? data : [];
  return rows
    .map((s) => {
      const raw = s.value ?? s.Value;
      const value = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
      const displayName = (s.displayName ?? s.DisplayName ?? '') as string;
      const name = (s.name ?? s.Name ?? '') as string;
      return { value, name, displayName };
    })
    .filter((s) => Number.isFinite(s.value));
}

// External Service Items API
export async function fetchExternalServiceItems(isActive?: boolean): Promise<ExternalServiceItemDto[]> {
  const params: any = {};
  if (isActive !== undefined) params.isActive = isActive;
  const { data } = await api.get<ExternalServiceItemDto[]>('/external-service-items', { params });
  return data;
}

// Complaint Items API
export async function fetchComplaintItems(isActive?: boolean): Promise<ComplaintItemDto[]> {
  const params: any = {};
  if (isActive !== undefined) params.isActive = isActive;
  const { data } = await api.get<ComplaintItemDto[]>('/complaint-items', { params });
  return data;
}

export async function createComplaintItem(item: {
  name: string;
  description?: string;
}): Promise<ComplaintItemDto> {
  const { data } = await api.post<ComplaintItemDto>('/complaint-items', item);
  return data;
}

export async function updateComplaintItem(id: number, item: {
  name: string;
  description?: string;
  isActive: boolean;
}): Promise<ComplaintItemDto> {
  const { data } = await api.put<ComplaintItemDto>(`/complaint-items/${id}`, item);
  return data;
}

export async function deleteComplaintItem(id: number): Promise<void> {
  await api.delete(`/complaint-items/${id}`);
}

export async function createExternalServiceItem(item: {
  name: string;
  description?: string;
  defaultPrice: number;
}): Promise<ExternalServiceItemDto> {
  const { data } = await api.post<ExternalServiceItemDto>('/external-service-items', item);
  return data;
}

export async function updateExternalServiceItem(id: number, item: {
  name: string;
  description?: string;
  defaultPrice: number;
  isActive: boolean;
}): Promise<ExternalServiceItemDto> {
  const { data } = await api.put<ExternalServiceItemDto>(`/external-service-items/${id}`, item);
  return data;
}

export async function deleteExternalServiceItem(id: number): Promise<void> {
  await api.delete(`/external-service-items/${id}`);
}

// Service Order Lines API
export const TAX_RATE_OPTIONS = [0, 1, 8, 10, 20];

export async function createServiceOrderLine(line: {
  orderId: number;
  lineNo?: number;
  itemType: number;
  serviceItemId?: number;
  externalServiceItemId?: number;
  externalServiceAccountId?: number;
  costPrice?: number;
  productId?: number;
  productVariantId?: number;
  technicianUserId?: number;
  quantity: number;
  unitPrice: number;
  taxRate?: number;
  notes?: string;
  allowNegativeStock?: boolean;
  periodicMaintenanceMonths?: number;
}): Promise<ServiceOrderLineDto> {
  const { data } = await api.post<ServiceOrderLineDto>('/serviceorderlines', line);
  return data;
}

export async function updateServiceOrderLine(id: number, line: {
  serviceItemId?: number;
  externalServiceItemId?: number;
  externalServiceAccountId?: number;
  costPrice?: number;
  productId?: number;
  productVariantId?: number;
  technicianUserId?: number;
  quantity: number;
  unitPrice: number;
  taxRate?: number;
  notes?: string;
  periodicMaintenanceMonths?: number;
}): Promise<ServiceOrderLineDto> {
  const { data } = await api.put<ServiceOrderLineDto>(`/serviceorderlines/${id}`, line);
  return data;
}

export async function deleteServiceOrderLine(id: number): Promise<void> {
  await api.delete(`/serviceorderlines/${id}`);
}

// Servis fişi bakiye (toplam borç, ödenen, kalan, cari bakiye)
export interface ServiceOrderBalanceDto {
  orderId: number;
  accountId: number;
  accountName: string;
  totalDebt: number;
  paidAmount: number;
  remainingBalance: number;
  accountBalance: number;
}

export async function fetchServiceOrderBalance(orderId: number): Promise<ServiceOrderBalanceDto> {
  const { data } = await api.get<ServiceOrderBalanceDto>(`/serviceorders/${orderId}/balance`);
  return data;
}

// Servis fişi tahsilatları
export interface ServiceOrderPaymentDto {
  id: number;
  serviceOrderId: number;
  accountId: number;
  paymentDate: string;
  paymentMethodId: number;
  paymentMethodName: string;
  amount: number;
  receiptNo?: string;
  description?: string;
  cashRegisterId?: number;
  cashRegisterName?: string;
  cashTransactionId?: number;
  createdAt: string;
}

export async function fetchServiceOrderPayments(orderId: number): Promise<ServiceOrderPaymentDto[]> {
  const { data } = await api.get<ServiceOrderPaymentDto[]>(`/service-orders/${orderId}/payments`);
  return data;
}

export interface ServiceOrderPaymentItemDto {
  paymentMethodId: number;
  amount: number;
  paymentDate?: string;
  receiptNo?: string;
  description?: string;
  cashRegisterId?: number;
}

export async function createServiceOrderPayments(
  orderId: number,
  payments: ServiceOrderPaymentItemDto[]
): Promise<{ message: string; payments: ServiceOrderPaymentDto[] }> {
  const { data } = await api.post<{ message: string; payments: ServiceOrderPaymentDto[] }>(
    `/service-orders/${orderId}/payments`,
    { payments }
  );
  return data;
}
